import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import { flexRender } from '@tanstack/react-table';
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from '../../../../components/common/app-table/ui';
import AppDateRange from '../../../../components/common/AppDateRange';
import useDefaultTable from '../../../../components/common/app-table/useDefaultTable';
import LoadingIndicator from '../../../../components/common/LoadingIndicator';
import EURLogsColumns from './Columns';
import { IEUR } from '../utils/types';
import { useGetEURSMRQuery, useImportEUR } from '../../../../hooks/useEURSMR';
import moment from 'moment';
import * as xlsx from 'xlsx';
import { useSnackBar } from '../../../../components/ToastProvider';
import { GetTemplateButton } from '../../../../components/common/primebmd-ui/DataButtons';
import MultipleUploadButton from '../../../../components/common/MultipleUploadButton';
import { GetTemplateEUR } from '../utils/exporting';
import { usePermission } from "../../../../hooks/usePermissions";
import { useAuth } from "../../../../components/AuthProvider";
import { Modules, Permissions } from "../../../Users/Roles/roles-utils";

const EURLogsTable = () => {
  const user = useAuth();
  const hasPermission = usePermission(Modules.eur.accessKey);
  const hasAddPermission = usePermission(
    Modules.eur.accessKey,
    Permissions.add
  );
  const hasImportPermission = usePermission(
    Modules.eur.accessKey,
    Permissions.import
  );
  const { showSnackBar } = useSnackBar();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const importData = useImportEUR();
  const eur = useGetEURSMRQuery(startDate, endDate);
  const data: IEUR[] = eur?.data === undefined ? [] : eur?.data;
  const navigate = useNavigate();
  
  const goToAddEURSMR = () => navigate('/eur/new');
  
  const { table, dataCount, searchFilter, pageIndex, pageSize } =
    useDefaultTable<Partial<IEUR>>(data, EURLogsColumns(), [{ id: "status", desc: false }],
  );

  if (user?.isLoading) return <LoadingIndicator show />;
  if (!hasPermission) return <Navigate to="/home" />;

  const emptyText = searchFilter ? 'No data found' : 'No data yet';
	
  const handleTemplate = () => {
      GetTemplateEUR([], 'Template EUR Data');
  }

  const errorALert = (error: string) => {
    showSnackBar(error, 'error');
  }

	const readUploadFile = (e : any) => {
		e.preventDefault();
    if(e.target.files.length === 1){
      if(e.target.files[0] != undefined){
        const checkExcelName = e.target.files[0].name.split("_");
        if (checkExcelName[0].toUpperCase() == 'TEMPLATE EUR DATA') {
          const reader = new FileReader();
          reader.onload = async (e) => {
            const data = e.target?.result;
            const workbook = xlsx.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json = xlsx.utils.sheet_to_json(worksheet, {raw: false});
            const jsonData: any =  json === undefined ? [] : json;
            
            if(jsonData.length !== 0){
            const fieldsToConvert = ["downHours", "startingReading", "endingReading"];
            const fieldsToString = ["date", "equipment", "operator", "location"]
            const convertFieldsToNumber = (data: any, fieldsToConvert: any) => {
              return data.map((item: any) => {
              const newItem = { ...item };
              fieldsToConvert.forEach((field: any) => {
                if (newItem[field] !== undefined) {
                newItem[field] = Number(newItem[field]);
                }
                else{
                  newItem[field] = "";
                }
              });
              return newItem;
              });
            };
            const convertFieldsToString = (data: any, fieldsToString: any) => {
              return data.map((item: any) => {
              const newItem = { ...item };
              fieldsToString.forEach((field: any) => {
                if (newItem[field] === undefined) {
                  newItem[field] = "";
                }
              });
              return newItem;
              });
            };
            const modifiedArrayNumber = await convertFieldsToNumber(jsonData, fieldsToConvert);
            const modifiedArrayString = await convertFieldsToString(modifiedArrayNumber, fieldsToString);
            importData.mutate(modifiedArrayString);
            }
            else errorALert("No data found on excel")
          };
          reader.readAsArrayBuffer(e.target.files[0]);
        }
        else errorALert("Invalid file. Filename must start with 'Template EUR Data'.")
      }
    }
    else errorALert("Please select one file only for upload");
    e.target.value = '';
	}

  if (eur?.isLoading) return <LoadingIndicator show />;
  return (
    <>
      {importData?.isLoading ?  <LoadingIndicator show /> : null}
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <Stack direction='column' mb={4} gap={.75} width="100%">
          <Typography fontSize={14} mt={2} fontWeight={700}>
            Date
          </Typography>
          <Stack direction="row" justifyContent="space-between" width="35%">
            <AppDateRange
              id="date-range"
              startDate={startDate}
              endDate={endDate}
              onDateChange={(s, e) => {
                setStartDate(moment(s).format("YYYY-MM-DD"))
                setEndDate(moment(e).format("YYYY-MM-DD"))
              }}
            />
          </Stack>
        </Stack>
        <Stack width="20%" direction="row" justifyContent='flex-end' mb={2} gap={1}>
      {hasImportPermission &&
        <>
          <GetTemplateButton onClick={handleTemplate} />
          <MultipleUploadButton
            accept=".xlsx, .xls"
            onChange={readUploadFile}
          />
        </>
      }
      {hasAddPermission && 
        <Button onClick={goToAddEURSMR}>
          Add EUR
        </Button>
      }
        </Stack>
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align='center'>
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody
              dataCount={dataCount}
              // loading={loading}
              emptyText={emptyText}
            >
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align='center'>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position='relative'>
          <TablePagination
            sx={{ bgcolor: 'white', borderTop: '1px solid #eee' }}
            component='div'
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
    </>
  );
}

export default EURLogsTable;